import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { CopyButton } from "../components/CopyButton.tsx";
import { Banner } from "../components/Status.tsx";
import { Icon } from "../components/Icon.tsx";
import { GrenadeGlyph } from "../components/GrenadeGlyph.tsx";
import { Logo } from "../components/Logo.tsx";
import { steamSignInUrl } from "../lib/api.ts";
import { errorText } from "../lib/errors.ts";
import { useDevSignIn, useInvitePreview, useMe } from "../lib/queries.ts";

/** Only paths on this site. */
export function safeNext(value: string | null): string {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return "/";
  return value;
}

function InviteHint({ code }: { code: string }) {
  const { t } = useTranslation();
  const preview = useInvitePreview(code);
  if (!preview.data) return null;
  return <Banner tone="info">{t("signIn.invitedTo", { team: preview.data.teamName })}</Banner>;
}

/** After Steam sign-in, for a player who is not on the allowlist yet. */
function NotAllowed({ steamId }: { steamId: string | null }) {
  const { t } = useTranslation();
  const id = steamId && /^7656\d{13}$/.test(steamId) ? steamId : null;
  return (
    <Banner tone="warning">
      <strong>{t("signIn.notAllowedTitle")}</strong>
      <br />
      {t("signIn.notAllowed")}
      {id && (
        <span className="signin-steamid">
          <span>{t("signIn.yourSteamId")}</span>
          <code className="mono">{id}</code>
          <CopyButton text={id} label={t("signIn.copyId")} />
        </span>
      )}
    </Banner>
  );
}

function DevSignIn({ next }: { next: string }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const devSignIn = useDevSignIn();
  const navigate = useNavigate();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    devSignIn.mutate(name, { onSuccess: () => navigate(next, { replace: true }) });
  };
  return (
    <form className="dev-signin" onSubmit={submit}>
      <h2 className="eyebrow">{t("signIn.devTitle")}</h2>
      <p className="muted small">{t("signIn.devHelp")}</p>
      <label className="field">
        <span>{t("signIn.devName")}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} minLength={2} maxLength={32} required />
      </label>
      {devSignIn.error && <p className="field-error">{errorText(t, devSignIn.error)}</p>}
      <button type="submit" className="btn btn-secondary" disabled={devSignIn.isPending}>
        {t("signIn.devSubmit")}
      </button>
    </form>
  );
}

export function SignInPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const me = useMe();
  const next = safeNext(params.get("next"));
  const inviteCode = /^\/join\/([A-Za-z0-9_-]{12})$/.exec(next)?.[1];

  if (me.data?.user) return <Navigate to={next} replace />;

  return (
    <div className="signin">
      <section className="signin-hero">
        <div className="signin-brand">
          <Logo height={80} />
          <p className="signin-product">{t("brand.name")}</p>
          <span className="eyebrow">{t("brand.tagline")}</span>
        </div>
        <h1 className="signin-title">{t("signIn.title")}</h1>
        <p className="signin-intro">{t("signIn.intro")}</p>
        <div className="signin-art" aria-hidden="true">
          {(["smoke", "flash", "molotov", "he"] as const).map((g) => (
            <span key={g} className={`glyph glyph-${g}`}>
              <GrenadeGlyph grenade={g} />
            </span>
          ))}
          <svg viewBox="0 0 400 160" className="signin-arcs">
            <path d="M20 140 Q120 10 230 70" />
            <circle cx="230" cy="70" r="10" />
            <path d="M120 150 Q220 60 360 40" />
            <circle cx="360" cy="40" r="10" />
          </svg>
        </div>
      </section>

      <section className="signin-panel" aria-labelledby="signin-title">
        <div className="signin-card">
          <h2 id="signin-title" className="section-title">
            {t("signIn.cardTitle")}
          </h2>
          <p className="muted">{t("signIn.cardIntro")}</p>
          {params.get("error") === "steam" && <Banner tone="warning">{t("signIn.steamError")}</Banner>}
          {params.get("error") === "not_allowed" && <NotAllowed steamId={params.get("steamId")} />}
          {inviteCode && <InviteHint code={inviteCode} />}
          <a className="btn btn-primary btn-large" href={steamSignInUrl(next)}>
            <Icon name="steam" size={20} />
            {t("signIn.steam")}
          </a>
          <ul className="point-list">
            <li>
              <Icon name="lock" />
              {t("signIn.pointPassword")}
            </li>
            <li>
              <Icon name="eye" />
              {t("signIn.pointData")}
            </li>
            <li>
              <Icon name="shield" />
              {t("signIn.pointGame")}
            </li>
          </ul>
          {!inviteCode && <p className="muted small">{t("signIn.inviteTip")}</p>}
          {me.data?.devSignIn && <DevSignIn next={next} />}
        </div>
      </section>
    </div>
  );
}
